/**
 * ===================================================================
 * REACT HOOK - HEALTH INCIDENTS
 * ===================================================================
 * Hook pro spravu zdravotnich incidentu s React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthIncidentService } from '../services/healthIncidentService';
import type {
  CreateHealthIncidentDTO,
  UpdateHealthIncidentDTO,
  HealthIncidentFilters,
  IncidentStatus,
} from '../types/health-incidents';

// Query keys
const QUERY_KEYS = {
  incidents: ['health-incidents'] as const,
  incident: (id: string) => ['health-incidents', id] as const,
  areaIncidents: (areaId: string) => ['health-incidents', 'area', areaId] as const,
  openIncidents: (areaId?: string) => ['health-incidents', 'open', areaId] as const,
  areaSummary: (areaId: string) => ['health-incidents', 'area', areaId, 'summary'] as const,
  systemOverview: ['health-incidents', 'overview'] as const,
};

/**
 * Hook pro ziskani incidentu podle ID
 */
export const useHealthIncident = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.incident(id || ''),
    queryFn: () => healthIncidentService.getById(id!),
    enabled: !!id,
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani incidentu pro oblast
 */
export const useAreaHealthIncidents = (
 areaId: string | undefined,
 filters?: HealthIncidentFilters,
) => {
 return useQuery({
   queryKey: [...QUERY_KEYS.areaIncidents(areaId || ''), filters],
   queryFn: () => healthIncidentService.getIncidentsByArea(areaId!, filters),
   enabled: !!areaId,
   staleTime: 30000,
 });
};

/**
 * Hook pro ziskani otevrenych incidentu
 */
export const useOpenHealthIncidents = (areaId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.openIncidents(areaId),
    queryFn: () => healthIncidentService.getOpenIncidents(areaId),
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani souhrnu zdravi oblasti
 */
export const useAreaHealthSummary = (areaId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.areaSummary(areaId || ''),
    queryFn: () => healthIncidentService.getAreaSummary(areaId!),
    enabled: !!areaId,
    staleTime: 60000,
  });
};

/**
 * Hook pro systemovy prehled zdravi
 */
export const useSystemHealthOverview = () => {
  return useQuery({
    queryKey: QUERY_KEYS.systemOverview,
    queryFn: () => healthIncidentService.getSystemOverview(),
    staleTime: 60000,
  });
};

/**
 * Hook pro vytvoreni incidentu
 */
export const useCreateHealthIncident = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateHealthIncidentDTO) => healthIncidentService.create(data),
    onSuccess: (incident) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaIncidents(incident.area_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.openIncidents(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaSummary(incident.area_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.systemOverview,
      });
    },
  });
};

/**
 * Hook pro aktualizaci incidentu
 */
export const useUpdateHealthIncident = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHealthIncidentDTO }) =>
      healthIncidentService.update(id, data),
    onSuccess: (incident) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.incident(incident.id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaIncidents(incident.area_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.openIncidents(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaSummary(incident.area_id),
      });
    },
  });
};

/**
 * Hook pro vyreseni incidentu
 */
export const useResolveHealthIncident = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, treatmentNotes }: { id: string; treatmentNotes?: string }) =>
      healthIncidentService.resolve(id, treatmentNotes || ''),
    onSuccess: (incident) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.incident(incident.id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaIncidents(incident.area_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.openIncidents(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaSummary(incident.area_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.systemOverview,
      });
    },
  });
};

/**
 * Hook pro smazani incidentu
 */
export const useDeleteHealthIncident = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, areaId }: { id: string; areaId: string }) =>
      healthIncidentService.delete(id).then(() => areaId),
    onSuccess: (areaId) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaIncidents(areaId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.openIncidents(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areaSummary(areaId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.systemOverview,
      });
    },
  });
};

/**
 * Hook pro zmenu stavu incidentu
 */
export const useChangeIncidentStatus = () => {
  const updateMutation = useUpdateHealthIncident();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: IncidentStatus }) =>
      updateMutation.mutateAsync({ id, data: { status } }),
  });
};
